import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    mcpConnectorService,
    type McpConnectorResponse,
    type McpOAuthSetupStatusResponse,
    type NormalizedMcpConnectorInput,
} from '@common/api/mcpConnectorService';
import { type McpOAuthReturnParams } from './mcpOAuthReturnParams';

export const MCP_OAUTH_RESULT_MESSAGE = 'bustly:mcp-oauth-result';
export const MCP_OAUTH_RESULT_CHANNEL = 'bustly:mcp-oauth-result';

const OAUTH_STATUS_POLL_MS = 1_000;
const POPUP_CLOSED_POLL_MS = 500;
const TERMINAL_SETUP_STATUSES = new Set(['ERROR', 'FAILED', 'CANCELLED', 'DENIED', 'EXPIRED']);

export interface McpOAuthReturn extends McpOAuthReturnParams {}

type WorkspaceBoundState = {
    originWorkspaceId: string;
};

export type McpConnectorFlowState =
    | { kind: 'idle' }
    | ({ kind: 'creating-draft' } & WorkspaceBoundState)
    | ({
        kind: 'awaiting-oauth';
        connectorId: number | string;
        setupId: string;
        expiresAt: string;
    } & WorkspaceBoundState)
    | ({ kind: 'checking-status'; connectorId: number | string; setupId: string } & WorkspaceBoundState)
    | ({ kind: 'refreshing'; connectorId: number | string } & WorkspaceBoundState)
    | ({ kind: 'manual-setup'; connectorId: number | string } & WorkspaceBoundState)
    | ({ kind: 'completed'; connectorId: number | string } & WorkspaceBoundState)
    | ({ kind: 'error'; reasonCode: string | null } & WorkspaceBoundState);

type PopupWindow = Pick<Window, 'closed' | 'close'> & { opener?: unknown };

type BroadcastChannelHandle = {
    onmessage: ((event: MessageEvent<unknown>) => void) | null;
    close: () => void;
};

type McpFlowService = Pick<
    typeof mcpConnectorService,
    'create' | 'startOAuth' | 'getOAuthSetupStatus'
>;

export interface McpConnectorRefreshRequest {
    originWorkspaceId: string;
    signal: AbortSignal;
    generation: number;
}

interface McpConnectorFlowDependencies {
    service: McpFlowService;
    refreshCurrentWorkspaceConnectors: (request: McpConnectorRefreshRequest) => Promise<void>;
    getCurrentWorkspaceId: () => string | null;
    openAuthorization: (authorizationUrl: string, features: string) => PopupWindow | null;
    createBroadcastChannel: () => BroadcastChannelHandle;
    now: () => number;
    onStateChange?: (state: McpConnectorFlowState) => void;
}

interface ActiveOAuthRequest {
    generation: number;
    originWorkspaceId: string;
    controller: AbortController;
    connectorId: number | string | null;
    setupId: string | null;
    expiresAt: string | null;
    expiresAtMs: number | null;
    popup: PopupWindow | null;
    channel: BroadcastChannelHandle | null;
    timeoutId: ReturnType<typeof setTimeout> | null;
    popupPollId: ReturnType<typeof setInterval> | null;
    statusPollId: ReturnType<typeof setTimeout> | null;
    returnConsumed: boolean;
}

export interface WorkspaceRequestGeneration {
    generation: number;
    originWorkspaceId: string;
    signal: AbortSignal;
    abort: () => void;
    isLatest: () => boolean;
    isCurrent: () => boolean;
}

export const createWorkspaceRequestGuard = (
    getCurrentWorkspaceId: () => string | null,
) => {
    let generation = 0;
    let controller: AbortController | null = null;

    return {
        begin(originWorkspaceId: string): WorkspaceRequestGeneration {
            controller?.abort();
            controller = new AbortController();
            const requestController = controller;
            const requestGeneration = ++generation;

            return {
                generation: requestGeneration,
                originWorkspaceId,
                signal: requestController.signal,
                abort: () => requestController.abort(),
                isLatest: () => (
                    generation === requestGeneration
                    && controller === requestController
                ),
                isCurrent: () => (
                    generation === requestGeneration
                    && controller === requestController
                    && !requestController.signal.aborted
                    && getCurrentWorkspaceId() === originWorkspaceId
                ),
            };
        },
        dispose() {
            generation += 1;
            controller?.abort();
            controller = null;
        },
    };
};

const readReasonCode = (error: unknown): string | null => {
    if (!error || typeof error !== 'object') {
        return null;
    }

    const value = error as {
        reasonCode?: unknown;
        code?: unknown;
        response?: { data?: { reasonCode?: unknown; data?: { reasonCode?: unknown } } };
    };
    const reasonCode = value.reasonCode
        ?? value.response?.data?.reasonCode
        ?? value.response?.data?.data?.reasonCode
        ?? value.code;
    return typeof reasonCode === 'string' ? reasonCode : null;
};

const isOAuthUnsupportedReason = (reasonCode: string | null) => (
    reasonCode === 'MCP_OAUTH_NOT_SUPPORTED'
    || reasonCode === 'MCP_OAUTH_UNSUPPORTED'
);

const isMcpOAuthReturn = (value: unknown): value is McpOAuthReturn & { type: string } => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const message = value as Record<string, unknown>;
    return message.type === MCP_OAUTH_RESULT_MESSAGE
        && typeof message.setupId === 'string'
        && (message.result === 'success' || message.result === 'error')
        && (typeof message.reasonCode === 'string' || message.reasonCode == null);
};

const defaultOpenAuthorization = (authorizationUrl: string, features: string): PopupWindow | null => {
    const popup = window.open(authorizationUrl, 'bustly-mcp-oauth', features);
    if (popup) {
        try {
            popup.opener = null;
        } catch {
            // Some browser contexts reject opener mutation; the popup handle is still required for OAuth polling.
        }
    }
    return popup;
};

const defaultCreateBroadcastChannel = (): BroadcastChannelHandle => (
    new BroadcastChannel(MCP_OAUTH_RESULT_CHANNEL)
);

export const createMcpConnectorFlow = (
    initialWorkspaceId: string | null,
    dependencies: McpConnectorFlowDependencies,
) => {
    let currentWorkspaceId = initialWorkspaceId;
    let state: McpConnectorFlowState = { kind: 'idle' };
    let generation = 0;
    let activeRequest: ActiveOAuthRequest | null = null;
    let disposed = false;

    const publishState = (nextState: McpConnectorFlowState) => {
        state = nextState;
        if (!disposed) {
            dependencies.onStateChange?.(nextState);
        }
    };

    const clearActiveResources = (request: ActiveOAuthRequest, closePopup: boolean) => {
        if (request.timeoutId) {
            clearTimeout(request.timeoutId);
            request.timeoutId = null;
        }
        if (request.popupPollId) {
            clearInterval(request.popupPollId);
            request.popupPollId = null;
        }
        if (request.statusPollId) {
            clearTimeout(request.statusPollId);
            request.statusPollId = null;
        }
        if (request.channel) {
            request.channel.onmessage = null;
            request.channel.close();
            request.channel = null;
        }
        if (closePopup && request.popup && !request.popup.closed) {
            request.popup.close();
        }
        request.popup = null;
        request.controller.abort();
    };

    const stopPopupMonitoring = (request: ActiveOAuthRequest, closePopup: boolean) => {
        if (request.popupPollId) {
            clearInterval(request.popupPollId);
            request.popupPollId = null;
        }
        if (closePopup && request.popup && !request.popup.closed) {
            request.popup.close();
        }
        request.popup = null;
    };

    const isCurrentRequest = (request: ActiveOAuthRequest) => (
        activeRequest === request
        && request.generation === generation
        && !request.controller.signal.aborted
        && currentWorkspaceId === request.originWorkspaceId
        && dependencies.getCurrentWorkspaceId() === request.originWorkspaceId
    );

    const finish = (
        request: ActiveOAuthRequest,
        nextState: McpConnectorFlowState,
        closePopup = true,
    ) => {
        if (!isCurrentRequest(request)) {
            return;
        }

        generation += 1;
        clearActiveResources(request, closePopup);
        activeRequest = null;
        publishState(nextState);
    };

    const cancelActive = (publishIdle: boolean) => {
        generation += 1;
        if (activeRequest) {
            clearActiveResources(activeRequest, true);
            activeRequest = null;
        }
        if (publishIdle) {
            publishState({ kind: 'idle' });
        }
    };

    const resolveSetupStatus = async (request: ActiveOAuthRequest): Promise<void> => {
        if (!isCurrentRequest(request) || !request.setupId || request.connectorId == null) {
            return;
        }

        request.statusPollId = null;
        try {
            const setupStatus: McpOAuthSetupStatusResponse = await dependencies.service.getOAuthSetupStatus(
                request.originWorkspaceId,
                request.setupId,
                request.controller.signal,
            );
            if (!isCurrentRequest(request)) {
                return;
            }

            if (
                String(setupStatus.setupId) !== request.setupId
                || String(setupStatus.connectorId) !== String(request.connectorId)
            ) {
                finish(request, {
                    kind: 'error',
                    reasonCode: 'MCP_OAUTH_STATUS_MISMATCH',
                    originWorkspaceId: request.originWorkspaceId,
                });
                return;
            }

            const status = String(setupStatus.status || '').toUpperCase();
            if (status === 'SUCCESS') {
                stopPopupMonitoring(request, true);
                publishState({
                    kind: 'refreshing',
                    connectorId: request.connectorId,
                    originWorkspaceId: request.originWorkspaceId,
                });
                try {
                    await dependencies.refreshCurrentWorkspaceConnectors({
                        originWorkspaceId: request.originWorkspaceId,
                        signal: request.controller.signal,
                        generation: request.generation,
                    });
                } catch {
                    if (!isCurrentRequest(request)) {
                        return;
                    }

                    const remainingMs = Math.max(0, (request.expiresAtMs || 0) - dependencies.now());
                    if (remainingMs === 0) {
                        finish(request, {
                            kind: 'error',
                            reasonCode: 'MCP_OAUTH_REFRESH_FAILED',
                            originWorkspaceId: request.originWorkspaceId,
                        });
                        return;
                    }

                    request.statusPollId = setTimeout(() => {
                        void resolveSetupStatus(request);
                    }, Math.min(OAUTH_STATUS_POLL_MS, remainingMs));
                    return;
                }

                if (isCurrentRequest(request)) {
                    finish(request, {
                        kind: 'completed',
                        connectorId: request.connectorId,
                        originWorkspaceId: request.originWorkspaceId,
                    });
                }
                return;
            }

            if (TERMINAL_SETUP_STATUSES.has(status)) {
                finish(request, {
                    kind: 'error',
                    reasonCode: setupStatus.reasonCode || `MCP_OAUTH_${status}`,
                    originWorkspaceId: request.originWorkspaceId,
                });
                return;
            }

            const remainingMs = Math.max(0, (request.expiresAtMs || 0) - dependencies.now());
            if (remainingMs === 0) {
                finish(request, {
                    kind: 'error',
                    reasonCode: 'MCP_OAUTH_TIMEOUT',
                    originWorkspaceId: request.originWorkspaceId,
                });
                return;
            }

            request.statusPollId = setTimeout(() => {
                void resolveSetupStatus(request);
            }, Math.min(OAUTH_STATUS_POLL_MS, remainingMs));
        } catch (error) {
            if (!isCurrentRequest(request)) {
                return;
            }
            finish(request, {
                kind: 'error',
                reasonCode: readReasonCode(error) || 'MCP_OAUTH_STATUS_FAILED',
                originWorkspaceId: request.originWorkspaceId,
            });
        }
    };

    const consumeOAuthReturn = (request: ActiveOAuthRequest, value: unknown) => {
        if (
            !isCurrentRequest(request)
            || request.returnConsumed
            || !isMcpOAuthReturn(value)
            || value.setupId !== request.setupId
        ) {
            return;
        }

        request.returnConsumed = true;
        if (request.popupPollId) {
            clearInterval(request.popupPollId);
            request.popupPollId = null;
        }
        if (request.channel) {
            request.channel.onmessage = null;
            request.channel.close();
            request.channel = null;
        }

        if (value.result === 'error') {
            finish(request, {
                kind: 'error',
                reasonCode: value.reasonCode,
                originWorkspaceId: request.originWorkspaceId,
            }, false);
            return;
        }

        stopPopupMonitoring(request, true);
        publishState({
            kind: 'checking-status',
            connectorId: request.connectorId!,
            setupId: request.setupId!,
            originWorkspaceId: request.originWorkspaceId,
        });
        void resolveSetupStatus(request);
    };

    return {
        get state(): McpConnectorFlowState {
            return state;
        },
        async startOAuth(
            input: NormalizedMcpConnectorInput | null,
            existingConnectorId?: number | string,
        ): Promise<McpConnectorResponse | null> {
            const originWorkspaceId = dependencies.getCurrentWorkspaceId();
            if (!originWorkspaceId || originWorkspaceId !== currentWorkspaceId || disposed || (!input && existingConnectorId == null)) {
                return null;
            }

            cancelActive(false);
            const request: ActiveOAuthRequest = {
                generation: ++generation,
                originWorkspaceId,
                controller: new AbortController(),
                connectorId: null,
                setupId: null,
                expiresAt: null,
                expiresAtMs: null,
                popup: null,
                channel: null,
                timeoutId: null,
                popupPollId: null,
                statusPollId: null,
                returnConsumed: false,
            };
            activeRequest = request;
            publishState({ kind: 'creating-draft', originWorkspaceId });

            let createdConnector: McpConnectorResponse | null = null;
            try {
                if (input) {
                    createdConnector = await dependencies.service.create(
                        originWorkspaceId,
                        input.createRequest,
                        request.controller.signal,
                    );
                    if (!isCurrentRequest(request)) {
                        return null;
                    }
                    request.connectorId = createdConnector.connectorId;
                } else {
                    request.connectorId = existingConnectorId!;
                }

                const oauth = await dependencies.service.startOAuth(
                    originWorkspaceId,
                    request.connectorId,
                    request.controller.signal,
                );
                if (!isCurrentRequest(request)) {
                    return null;
                }

                const expiresAtMs = Date.parse(oauth.expiresAt);
                if (!Number.isFinite(expiresAtMs) || expiresAtMs <= dependencies.now()) {
                    finish(request, {
                        kind: 'error',
                        reasonCode: 'MCP_OAUTH_TIMEOUT',
                        originWorkspaceId,
                    });
                    return null;
                }

                request.setupId = oauth.setupId;
                request.expiresAt = oauth.expiresAt;
                request.expiresAtMs = expiresAtMs;
                request.channel = dependencies.createBroadcastChannel();
                request.channel.onmessage = (event: MessageEvent<unknown>) => {
                    consumeOAuthReturn(request, event.data);
                };

                const popupFeatures = 'popup=yes,width=640,height=760';
                request.popup = dependencies.openAuthorization(oauth.authorizationUrl, popupFeatures);
                if (!request.popup) {
                    finish(request, {
                        kind: 'error',
                        reasonCode: 'MCP_OAUTH_POPUP_BLOCKED',
                        originWorkspaceId,
                    });
                    return null;
                }

                request.timeoutId = setTimeout(() => {
                    finish(request, {
                        kind: 'error',
                        reasonCode: 'MCP_OAUTH_TIMEOUT',
                        originWorkspaceId,
                    });
                }, expiresAtMs - dependencies.now());
                request.popupPollId = setInterval(() => {
                    if (request.popup?.closed) {
                        stopPopupMonitoring(request, false);
                        if (request.statusPollId) {
                            clearTimeout(request.statusPollId);
                            request.statusPollId = null;
                        }
                        publishState({
                            kind: 'checking-status',
                            connectorId: request.connectorId!,
                            setupId: request.setupId!,
                            originWorkspaceId,
                        });
                        void resolveSetupStatus(request);
                    }
                }, POPUP_CLOSED_POLL_MS);
                request.statusPollId = setTimeout(() => {
                    void resolveSetupStatus(request);
                }, Math.min(OAUTH_STATUS_POLL_MS, expiresAtMs - dependencies.now()));

                publishState({
                    kind: 'awaiting-oauth',
                    connectorId: request.connectorId,
                    setupId: oauth.setupId,
                    expiresAt: oauth.expiresAt,
                    originWorkspaceId,
                });
                return createdConnector;
            } catch (error) {
                if (!isCurrentRequest(request)) {
                    return null;
                }

                const reasonCode = readReasonCode(error);
                if (isOAuthUnsupportedReason(reasonCode) && createdConnector) {
                    finish(request, {
                        kind: 'manual-setup',
                        connectorId: createdConnector.connectorId,
                        originWorkspaceId,
                    });
                    return createdConnector;
                }

                finish(request, {
                    kind: 'error',
                    reasonCode,
                    originWorkspaceId,
                });
                return null;
            }
        },
        switchWorkspace(nextWorkspaceId: string | null) {
            if (nextWorkspaceId === currentWorkspaceId) {
                return;
            }
            currentWorkspaceId = nextWorkspaceId;
            cancelActive(true);
        },
        close() {
            cancelActive(true);
        },
        dispose() {
            if (disposed) {
                return;
            }
            disposed = true;
            cancelActive(false);
        },
    };
};

export interface UseMcpConnectorFlowOptions {
    workspaceId: string | null;
    refreshCurrentWorkspaceConnectors: (request: McpConnectorRefreshRequest) => Promise<void>;
    onFlowError?: (reasonCode: string | null) => void;
}

export const useMcpConnectorFlow = ({
    workspaceId,
    refreshCurrentWorkspaceConnectors,
    onFlowError,
}: UseMcpConnectorFlowOptions) => {
    const currentWorkspaceIdRef = useRef(workspaceId);
    currentWorkspaceIdRef.current = workspaceId;
    const refreshRef = useRef(refreshCurrentWorkspaceConnectors);
    const onFlowErrorRef = useRef(onFlowError);
    const [state, setState] = useState<McpConnectorFlowState>({ kind: 'idle' });
    const flowRef = useRef<ReturnType<typeof createMcpConnectorFlow> | null>(null);

    refreshRef.current = refreshCurrentWorkspaceConnectors;
    onFlowErrorRef.current = onFlowError;

    if (!flowRef.current) {
        flowRef.current = createMcpConnectorFlow(workspaceId, {
            service: mcpConnectorService,
            refreshCurrentWorkspaceConnectors: request => refreshRef.current(request),
            getCurrentWorkspaceId: () => currentWorkspaceIdRef.current,
            openAuthorization: defaultOpenAuthorization,
            createBroadcastChannel: defaultCreateBroadcastChannel,
            now: () => Date.now(),
            onStateChange: (nextState) => {
                setState(nextState);
                if (nextState.kind === 'error') {
                    onFlowErrorRef.current?.(nextState.reasonCode);
                }
            },
        });
    }

    useEffect(() => {
        flowRef.current?.switchWorkspace(workspaceId);
    }, [workspaceId]);

    useEffect(() => () => {
        flowRef.current?.dispose();
    }, []);

    const startOAuth = useCallback((input: NormalizedMcpConnectorInput) => (
        flowRef.current!.startOAuth(input)
    ), []);
    const startExistingOAuth = useCallback((connectorId: number | string) => (
        flowRef.current!.startOAuth(null, connectorId)
    ), []);
    const close = useCallback(() => {
        flowRef.current?.close();
    }, []);

    return useMemo(() => ({
        state,
        startOAuth,
        startExistingOAuth,
        close,
    }), [close, startExistingOAuth, startOAuth, state]);
};
